import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, PawPrint, Download } from "lucide-react";
import { getCuisines, getRecipes, API } from "@/lib/api";
import { CUISINE_ORDER, metaFor } from "@/lib/cuisines";
import { RecipeCard } from "@/components/RecipeCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1690983322070-22861e13ce47?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [cuisines, setCuisines] = useState([]);
  const [recipesByCuisine, setRecipesByCuisine] = useState({});
  const [petRecipes, setPetRecipes] = useState([]);

  useEffect(() => {
    getCuisines().then(setCuisines);
    getRecipes().then((all) => {
      const grouped = {};
      all.forEach((r) => {
        (grouped[r.cuisine] = grouped[r.cuisine] || []).push(r);
      });
      setRecipesByCuisine(grouped);
    });
    getRecipes({ cuisine: "pet" }).then(setPetRecipes);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/cuisine/all?q=${encodeURIComponent(query.trim())}`);
  };

  const cuisineInfo = (key) => cuisines.find((c) => c.key === key);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-6">
              One pan · four cuisines · zero fuss
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95] text-gray-900">
              Great food,
              <br />
              <span className="italic">humble kitchen.</span>
            </h1>
            <p className="mt-8 text-lg text-gray-600 font-light leading-relaxed max-w-md">
              A cookbook of cheap, beginner-friendly dishes from India, Italy, China and
              Mexico. Every recipe explains its spices and where it comes from.
            </p>

            <form
              onSubmit={submitSearch}
              className="mt-10 flex items-center border border-gray-900 max-w-md"
            >
              <Search className="h-5 w-5 text-gray-400 ml-4" />
              <input
                data-testid="home-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by dish or ingredient..."
                className="flex-1 px-3 py-4 text-base outline-none bg-transparent placeholder:text-gray-400"
              />
              <button
                type="submit"
                data-testid="home-search-button"
                className="bg-gray-900 text-white px-5 py-4 text-sm font-medium hover:bg-gray-700 transition-colors"
              >
                Search
              </button>
            </form>

            <a
              href={`${API}/cookbook.pdf`}
              data-testid="download-cookbook-button"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gray-900 border-b-2 border-gray-900 pb-1 hover:gap-3 transition-all"
            >
              <Download className="h-4 w-4" />
              Download the full cookbook (PDF)
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Fresh ingredients on a cutting board"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cuisine intro cards */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {CUISINE_ORDER.map((key, i) => {
            const meta = metaFor(key);
            const info = cuisineInfo(key);
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <Link
                  to={`/cuisine/${key}`}
                  data-testid={`home-cuisine-card-${key}`}
                  className={`group block h-full border ${meta.border} ${meta.sectionBg} p-6 md:p-8 hover:border-gray-900 transition-colors`}
                >
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                  <h3 className="font-heading text-3xl font-medium mt-4 text-gray-900">
                    {meta.name}
                  </h3>
                  <p className={`text-xs font-semibold uppercase tracking-widest mt-1 ${meta.text}`}>
                    {info?.recipe_count ?? "—"} recipes
                  </p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-gray-900 group-hover:gap-2 transition-all">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* About the author / introduction */}
      <section
        data-testid="about-author-section"
        className="border-t border-gray-100 bg-gray-50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-5">
                <div className="h-20 w-20 rounded-full bg-gray-900 text-white flex items-center justify-center font-heading text-3xl">
                  AG
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-1">
                    About the author
                  </p>
                  <p className="font-heading text-3xl text-gray-900 leading-none">
                    Aarav Gautam
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-gray-900 mb-8">
                Hi there! I&rsquo;m Aarav Gautam.
              </h2>
              <div className="space-y-6 text-lg text-gray-600 font-light leading-relaxed">
                <p>
                  I&rsquo;m a student on a mission to prove that eating well doesn&rsquo;t
                  require a culinary degree, a massive budget, or a kitchen full of fancy
                  gadgets.
                </p>
                <p>
                  Like many teens and young adults, I know how easy it is to default to fast
                  food when life gets busy. I&rsquo;m creating this cookbook to change that. My
                  goal is to help you build confidence in the kitchen by breaking down simple,
                  accessible techniques and sharing recipes that are healthy, genuinely
                  delicious, and kind to your wallet.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Per-cuisine sections */}
      {CUISINE_ORDER.map((key) => {
        const meta = metaFor(key);
        const info = cuisineInfo(key);
        const recipes = (recipesByCuisine[key] || []).slice(0, 3);
        if (!info) return null;
        return (
          <section
            key={key}
            data-testid={`home-section-${key}`}
            className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 border-t border-gray-100"
          >
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 mb-12">
              <div className="lg:col-span-5">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500">
                    {meta.name} Kitchen
                  </span>
                </div>
                <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-gray-900 mb-5">
                  {info.tagline}
                </h2>
                <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed">
                  {info.blurb}
                </p>
                <Link
                  to={`/cuisine/${key}`}
                  data-testid={`home-viewall-${key}`}
                  className="mt-8 inline-flex items-center gap-2 border-b-2 border-gray-900 pb-1 text-sm font-semibold text-gray-900 hover:gap-3 transition-all"
                >
                  View all {info.recipe_count} {meta.name} recipes
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-3 gap-6">
                {recipes.map((r, idx) => (
                  <RecipeCard key={r.id} recipe={r} index={idx} />
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Pet-Friendly area */}
      {petRecipes.length > 0 && (
        <section
          data-testid="home-pet-section"
          className="border-t border-gray-100 bg-sky-50"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div className="max-w-xl">
                <div className="flex items-center gap-3 mb-4">
                  <PawPrint className="h-5 w-5 text-sky-600" />
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-sky-700">
                    For your furry friends
                  </span>
                </div>
                <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-gray-900 mb-4">
                  Pet-Friendly Kitchen
                </h2>
                <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed">
                  A few simple, unseasoned meals that are safe for dogs and cats — no salt,
                  oil, onion, garlic or spices. Always cool before serving.
                </p>
              </div>
              <Link
                to="/cuisine/pet"
                data-testid="home-viewall-pet"
                className="inline-flex items-center gap-2 border-b-2 border-sky-600 pb-1 text-sm font-semibold text-sky-700 hover:gap-3 transition-all whitespace-nowrap"
              >
                View all pet recipes
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {petRecipes.map((r, idx) => (
                <RecipeCard key={r.id} recipe={r} index={idx} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
